/**
 * SubscriptionsPanel — admin oversight + plan manager for driver subscriptions (#10).
 *
 * Two sections:
 *   1. Plan Manager — create/edit/activate the weekly & monthly access plans
 *      (pricing is configured here; defaults seed inactive at £0).
 *   2. Subscriptions — revenue summary cards + a table of every driver's
 *      subscription (plan, interval, status, next billing date).
 *
 * Display/oversight only — drivers self-serve subscribe & cancel. Nothing here
 * affects dispatch/lifecycle.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, CreditCard, TrendingUp, Users, RefreshCw, Check, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const gbp = (n) => `£${Number(n ?? 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const STATUS_CLS = {
  active: "bg-[#DCFCE7] text-[#166534]",
  past_due: "bg-[#FEF3C7] text-[#92400E]",
  canceled: "bg-[#FEE2E2] text-[#991B1B]",
  incomplete: "bg-secondary text-muted-foreground",
};

const SummaryCard = ({ label, value, accent = "#0A0A0A", icon: Icon, testid }) => (
  <div className="bg-white border border-border p-4" data-testid={testid}>
    <div className="label-mini text-muted-foreground flex items-center gap-1.5">{Icon && <Icon className="w-3 h-3" />} {label}</div>
    <div className="font-heading text-2xl font-black mt-1" style={{ color: accent }}>{value}</div>
  </div>
);

const PlanRow = ({ plan, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(String(plan.amount_gbp ?? 0));
  const [name, setName] = useState(plan.name);
  const [saving, setSaving] = useState(false);

  const save = async (patch) => {
    setSaving(true);
    try {
      await onSave(plan.id, patch);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-border p-4 flex items-center justify-between gap-3" data-testid={`plan-row-${plan.id}`}>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex flex-wrap items-center gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} className="px-2 py-1 border border-border text-sm w-40" data-testid={`plan-name-input-${plan.id}`} />
            <span className="text-xs text-muted-foreground uppercase">{plan.interval}ly · £</span>
            <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="px-2 py-1 border border-border text-sm w-24" data-testid={`plan-amount-input-${plan.id}`} />
          </div>
        ) : (
          <>
            <div className="font-semibold">{plan.name}</div>
            <div className="text-sm text-muted-foreground capitalize">{plan.interval}ly · {gbp(plan.amount_gbp)}</div>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {editing ? (
          <>
            <button onClick={() => save({ name, amount_gbp: parseFloat(amount) || 0 })} disabled={saving} className="p-1.5 border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white" data-testid={`plan-save-${plan.id}`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            </button>
            <button onClick={() => setEditing(false)} className="p-1.5 border border-border hover:bg-secondary" data-testid={`plan-cancel-edit-${plan.id}`}>
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} className="text-xs px-2 py-1 border border-border hover:bg-secondary" data-testid={`plan-edit-${plan.id}`}>Edit</button>
            <button
              onClick={() => save({ active: !plan.active })}
              disabled={saving || (!plan.active && (plan.amount_gbp ?? 0) <= 0)}
              title={!plan.active && (plan.amount_gbp ?? 0) <= 0 ? "Set a price above £0 before activating" : ""}
              className={`text-xs px-3 py-1 border font-semibold disabled:opacity-40 ${plan.active ? "border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white" : "border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white"}`}
              data-testid={`plan-toggle-active-${plan.id}`}
            >
              {plan.active ? "Deactivate" : "Activate"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const SubscriptionsPanel = () => {
  const [plans, setPlans] = useState([]);
  const [subsData, setSubsData] = useState(null);
  const [retPlans, setRetPlans] = useState([]);
  const [retSubsData, setRetSubsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newPlan, setNewPlan] = useState({ name: "", interval: "week", amount_gbp: "" });

  const load = useCallback(async () => {
    setLoading(true);
    // Settle each fetch independently so a single transient failure (e.g. a
    // slow retainer call) can't blank out the whole panel or leave a just-
    // toggled plan showing a stale Activate/Deactivate label after a refresh.
    const [p, s, rp, rs] = await Promise.allSettled([
      api.get("/admin/subscription-plans"),
      api.get("/admin/subscriptions"),
      api.get("/admin/corporate/retainer-plans"),
      api.get("/admin/corporate/retainer-subscriptions"),
    ]);
    if (p.status === "fulfilled") setPlans(p.value.data);
    if (s.status === "fulfilled") setSubsData(s.value.data);
    if (rp.status === "fulfilled") setRetPlans(rp.value.data);
    if (rs.status === "fulfilled") setRetSubsData(rs.value.data);
    const firstErr = [p, s, rp, rs].find((r) => r.status === "rejected");
    if (firstErr) toast.error(formatApiError(firstErr.reason));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePlan = async (planId, patch) => {
    try {
      await api.put(`/admin/subscription-plans/${planId}`, patch);
      toast.success("Plan updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  const saveRetainerPlan = async (planId, patch) => {
    try {
      await api.put(`/admin/corporate/retainer-plans/${planId}`, patch);
      toast.success("Retainer tier updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  const createPlan = async () => {
    if (!newPlan.name.trim()) { toast.error("Enter a plan name"); return; }
    setCreating(true);
    try {
      await api.post("/admin/subscription-plans", {
        name: newPlan.name.trim(),
        interval: newPlan.interval,
        amount_gbp: parseFloat(newPlan.amount_gbp) || 0,
        active: false,
      });
      toast.success("Plan created");
      setNewPlan({ name: "", interval: "week", amount_gbp: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div className="p-10 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  }

  const summary = subsData?.summary || {};
  const subs = subsData?.subscriptions || [];
  const retSummary = retSubsData?.summary || {};
  const retSubs = retSubsData?.subscriptions || [];

  return (
    <div className="p-4 space-y-6" data-testid="subscriptions-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-black tracking-tight">DRIVER SUBSCRIPTIONS</h2>
        <button onClick={load} className="text-xs px-2 py-1 border border-border hover:bg-secondary flex items-center gap-1" data-testid="subscriptions-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>

      {/* Revenue summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="ESTIMATED MRR" value={gbp(summary.estimated_mrr)} accent="#002FA7" icon={TrendingUp} testid="summary-mrr" />
        <SummaryCard label="ACTIVE" value={summary.active ?? 0} accent="#16A34A" icon={Users} testid="summary-active" />
        <SummaryCard label="PAST DUE" value={summary.past_due ?? 0} accent="#F59E0B" testid="summary-past-due" />
        <SummaryCard label="CANCELLED" value={summary.canceled ?? 0} accent="#EF4444" testid="summary-canceled" />
      </div>

      {/* Plan manager */}
      <div className="space-y-3">
        <h3 className="label-mini text-muted-foreground">PLAN MANAGER</h3>
        {plans.map((p) => <PlanRow key={p.id} plan={p} onSave={savePlan} />)}

        <div className="bg-secondary/30 border border-dashed border-border p-4 flex flex-wrap items-end gap-2" data-testid="new-plan-form">
          <div>
            <label className="label-mini text-muted-foreground">Name</label>
            <input value={newPlan.name} onChange={(e) => setNewPlan((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Premium Weekly" className="block px-2 py-1 border border-border text-sm w-44 mt-1" data-testid="new-plan-name" />
          </div>
          <div>
            <label className="label-mini text-muted-foreground">Interval</label>
            <select value={newPlan.interval} onChange={(e) => setNewPlan((s) => ({ ...s, interval: e.target.value }))} className="block px-2 py-1 border border-border text-sm mt-1" data-testid="new-plan-interval">
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
          <div>
            <label className="label-mini text-muted-foreground">Amount £</label>
            <input type="number" step="0.01" min="0" value={newPlan.amount_gbp} onChange={(e) => setNewPlan((s) => ({ ...s, amount_gbp: e.target.value }))} placeholder="0.00" className="block px-2 py-1 border border-border text-sm w-24 mt-1" data-testid="new-plan-amount" />
          </div>
          <Button onClick={createPlan} disabled={creating} size="sm" data-testid="new-plan-create">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add plan</>}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">New plans start inactive. Set a price above £0, then Activate to make them available to drivers. Changing a price mints a fresh Stripe price for new subscribers; existing subscribers keep their original price.</p>
      </div>

      {/* Subscriptions table */}
      <div className="space-y-3">
        <h3 className="label-mini text-muted-foreground">ALL SUBSCRIPTIONS ({subs.length})</h3>
        {subs.length === 0 ? (
          <div className="bg-white border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="subscriptions-empty">No driver subscriptions yet.</div>
        ) : (
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left">
                <tr>
                  <th className="px-3 py-2 label-mini text-muted-foreground">DRIVER</th>
                  <th className="px-3 py-2 label-mini text-muted-foreground">PLAN</th>
                  <th className="px-3 py-2 label-mini text-muted-foreground">INTERVAL</th>
                  <th className="px-3 py-2 label-mini text-muted-foreground">STATUS</th>
                  <th className="px-3 py-2 label-mini text-muted-foreground">NEXT BILLING</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t border-border" data-testid={`subscription-row-${s.driver_id}`}>
                    <td className="px-3 py-2 font-semibold">{s.driver_name || s.driver_id?.substring(0, 8)}</td>
                    <td className="px-3 py-2">{s.plan_name} · {gbp(s.amount_gbp)}</td>
                    <td className="px-3 py-2 capitalize">{s.interval}ly</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs font-bold px-2 py-0.5 ${STATUS_CLS[s.status] || STATUS_CLS.incomplete}`}>
                        {s.status}{s.cancel_at_period_end ? " (ending)" : ""}
                      </span>
                    </td>
                    <td className="px-3 py-2">{fmtDate(s.current_period_end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Corporate retainer plans (Subscriptions Phase 2) ── */}
      <div className="space-y-3 border-t-2 border-border pt-6" data-testid="retainer-section">
        <h2 className="font-heading text-lg font-black tracking-tight">CORPORATE RETAINERS</h2>
        <p className="text-xs text-muted-foreground">
          Flat recurring retainer tiers for corporate accounts (priority service / SLA), billed separately
          from and in addition to per-ride account charges. Activate a tier, then assign it to a company in
          the Corporate tab.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="RETAINER MRR" value={gbp(retSummary.estimated_mrr)} accent="#002FA7" icon={TrendingUp} testid="retainer-summary-mrr" />
          <SummaryCard label="ACTIVE" value={retSummary.active ?? 0} accent="#16A34A" icon={Users} testid="retainer-summary-active" />
          <SummaryCard label="PAST DUE" value={retSummary.past_due ?? 0} accent="#F59E0B" testid="retainer-summary-past-due" />
          <SummaryCard label="CANCELLED" value={retSummary.canceled ?? 0} accent="#EF4444" testid="retainer-summary-canceled" />
        </div>

        <div className="space-y-3">
          <h3 className="label-mini text-muted-foreground">RETAINER TIERS</h3>
          {retPlans.map((p) => <PlanRow key={p.id} plan={p} onSave={saveRetainerPlan} />)}
          <p className="text-xs text-muted-foreground">
            Tiers seed inactive (Bronze £99 · Silver £249 · Gold £499 / month). Set a price above £0, then
            Activate to make a tier assignable to companies.
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="label-mini text-muted-foreground">COMPANIES ON RETAINER ({retSubs.length})</h3>
          {retSubs.length === 0 ? (
            <div className="bg-white border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="retainer-subs-empty">No company retainers yet.</div>
          ) : (
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left">
                  <tr>
                    <th className="px-3 py-2 label-mini text-muted-foreground">COMPANY</th>
                    <th className="px-3 py-2 label-mini text-muted-foreground">TIER</th>
                    <th className="px-3 py-2 label-mini text-muted-foreground">STATUS</th>
                    <th className="px-3 py-2 label-mini text-muted-foreground">NEXT BILLING</th>
                  </tr>
                </thead>
                <tbody>
                  {retSubs.map((s) => (
                    <tr key={s.id} className="border-t border-border" data-testid={`retainer-sub-row-${s.company_id}`}>
                      <td className="px-3 py-2 font-semibold">{s.company_name || s.company_id?.substring(0, 8)}</td>
                      <td className="px-3 py-2">{s.plan_name} · {gbp(s.amount_gbp)}/{s.interval}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-bold px-2 py-0.5 ${STATUS_CLS[s.status] || STATUS_CLS.incomplete}`}>
                          {s.status}{s.cancel_at_period_end ? " (ending)" : ""}
                        </span>
                      </td>
                      <td className="px-3 py-2">{fmtDate(s.current_period_end)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsPanel;
